import React, {useEffect, useState} from 'react';
import {Modal, Button, Form, Dropdown, Label} from 'semantic-ui-react';
import {useUtilityAccounts} from '../../../providers/UtilityAccountProvider';
import './UtilityAccounts.css';

const dateFormatter = date => {
  if (!date) return '';
  if (date instanceof Date) {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  return date.toString().split('T')[0];
};

const twoDecimalFormatter = num => {
  return parseFloat(num).toFixed(2);
};

const rateUnitsOptions = [
  {
    key: 'MCF',
    text: 'MCF',
    value: 'MCF',
  },
  {
    key: 'kWh',
    text: 'kWh',
    value: 'kWh',
  },
]


export const UtilityRatesModal = ({ratesOpen, setRatesOpen, setRatesHasChanges, selectedUtilityAccount}) => {
  const [utilityRates, setUtilityRates] = useState([]);
  const [isEditActive, setIsEditActive] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);

  const [editId, setEditId] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editRate, setEditRate] = useState('');
  const [editUnit, setEditUnit] = useState('');
  const [editNotes, setEditNotes] = useState('');

  const {updateUtilityAccountRates, createUtilityAccountRates, deleteUtilityAccountRates} =
    useUtilityAccounts();

  useEffect(() => {
    if (typeof selectedUtilityAccount !== 'undefined') {
      try {
        setUtilityRates(selectedUtilityAccount.utilityRates);
        setRatesHasChanges(false);
      } catch {}
    }
  }, [selectedUtilityAccount, setRatesHasChanges]);

  const handleAddRate = () => {
    const tempId = 'new';
    const emptyRate = {
      id: tempId,
      utility_account_id: selectedUtilityAccount.id,
      effective_date: dateFormatter(new Date()),
      utility_rate: '',
      utility_rate_unit: '',
      notes: '',
    };
    setUtilityRates(utilityRates => [...utilityRates, emptyRate]);
    setEditId(tempId);
    setEditDate(emptyRate.effective_date);
    setEditRate('');
    setEditUnit(selectedUtilityAccount.type === 'electric' ? 'kWh' : 'MCF');
    setEditNotes('');
    setIsEditActive(true);
    setIsAddingNew(true);
  };

  const handleDelete = async (rateId) => {
    try {
      const success = await deleteUtilityAccountRates(rateId);
      if (success) {
        let utilityRatesCopy = [...utilityRates];
        let rateIndex = utilityRatesCopy.findIndex(rate => rate.id === rateId);
        utilityRatesCopy.splice(rateIndex, 1);
        setUtilityRates(utilityRatesCopy);
        setRatesHasChanges(true);
      }
    } catch (error) {
      console.error('Error deleting rate:', error);
      alert('Failed to delete rate. Please try again.');
    }
  };

  const updateUtilityAccountRate = (rateId, rateField, value) => {
    let utilityRatesCopy = [...utilityRates];
    let rateIndex = utilityRatesCopy.findIndex(rate => rate.id === rateId);
    let objectCopy = {...utilityRatesCopy[rateIndex]};

    objectCopy[rateField] = value;

    utilityRatesCopy[rateIndex] = objectCopy;

    setUtilityRates(utilityRatesCopy);
  };

  const decimalValidator = value => {
    if (value.match(/^\d+(\.\d+)?$/) != null) {
      return true;
    } else {
      alert('Please enter a valid decimal value.');
      return false;
    }
  };
  
  const handleEdit = (rate) => {
    setEditId(rate.id);
    setEditDate(rate.effective_date);
    setEditRate(rate.utility_rate);
    setEditUnit(rate.utility_rate_unit);
    setEditNotes(rate.notes);
    
    setIsEditActive(true);
  }
  
  const handleSave = async () => {
    if (!editDate.trim() || !editRate || !editUnit.trim()) {
      alert('Please fill in all required fields (Date, Rate, and Unit).');
      return;
    }

    if (!decimalValidator(editRate.toString())) {
      return;
    }

    try {
      if (isAddingNew) {
        const newRate = {
          id: 0,
          utility_account_id: selectedUtilityAccount.id,
          effective_date: editDate,
          utility_rate: parseFloat(editRate),
          utility_rate_unit: editUnit,
          notes: editNotes,
        };
        const createRateId = await createUtilityAccountRates(newRate);
        if (createRateId !== 0) {
          const savedRate = {
            id: createRateId,
            utility_account_id: selectedUtilityAccount.id,
            effective_date: editDate,
            utility_rate: parseFloat(editRate),
            utility_rate_unit: editUnit,
            notes: editNotes,
          };

          setUtilityRates(rates => {
            const filtered = rates.filter(rate => rate.id !== 'new');
            return [...filtered, savedRate];
          });

          setRatesHasChanges(true);
          resetEdit();
        }
      } else {
        const originalRate = utilityRates.find(r => r.id === editId);

        if (dateFormatter(editDate) !== dateFormatter(originalRate.effective_date)) {
          await updateUtilityAccountRates({
            utilityRateId: editId,
            effective_date: editDate
          });
          updateUtilityAccountRate(editId, 'effective_date', editDate);
          setRatesHasChanges(true);
        }

        if (parseFloat(editRate) !== parseFloat(originalRate.utility_rate)) {
          await updateUtilityAccountRates({
            utilityRateId: editId,
            utility_rate: parseFloat(editRate)
          });
          updateUtilityAccountRate(editId, 'utility_rate', parseFloat(editRate));
          setRatesHasChanges(true);
        }

        if (editUnit !== originalRate.utility_rate_unit) {
          await updateUtilityAccountRates({
            utilityRateId: editId,
            utility_rate_unit: editUnit
          });
          updateUtilityAccountRate(editId, 'utility_rate_unit', editUnit);
          setRatesHasChanges(true);
        }

        if (editNotes !== (originalRate.notes || '')) {
          await updateUtilityAccountRates({
            utilityRateId: editId,
            notes: editNotes
          });
          updateUtilityAccountRate(editId, 'notes', editNotes);
          setRatesHasChanges(true);
        }

        resetEdit();
      }
    } catch (error) {
      console.error('Error saving rate: ', error);
      alert('Failed to save changes. Please try again.');
    }
  }
  
  const resetEdit = () => {
    if (isAddingNew) {
      setUtilityRates(utilityRates => utilityRates.filter(rate => rate.id !== 'new'));
    }
    setIsEditActive(false);
    setIsAddingNew(false);

    setEditId('');
    setEditDate('');
    setEditRate('');
    setEditUnit('');
    setEditNotes('');
  }

  return (
    <Modal
      closeIcon
      onClose={() => {
        setRatesOpen(false)
        resetEdit()
      }}
      onOpen={() => setRatesOpen(true)}
      open={ratesOpen}
      style={{textAlign: 'left', width: '55vw'}}
    >
      <Modal.Header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Rates</span>
          <Button positive onClick={() => handleAddRate()} disabled={isEditActive}>
            Add Rate
          </Button>
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className="utility-accounts-container">
          <Form>
            <table className="utility-rates-grid">
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="number-column">Rate per unit $</th>
                  <th>Unit</th>
                  <th>Note</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
              
              {!utilityRates || utilityRates.length === 0 ? (
                  <td colSpan="5" className="empty-state">
                    No utility rates found. Click "Add Rate" to create one.
                  </td>
              ) : (utilityRates.map((rate) => {
                if (isEditActive && editId === rate.id) {
                  return (
                    <tr className="edit-row">
                      <td>
                        <Form.Input
                            name={`date-${rate.id}`}
                            type={'date'}
                            value={dateFormatter(editDate)}
                            placeholder="Time Range"
                            onChange={e => setEditDate(e.target.value)}
                        />
                      </td>
                      <td className="number-column">
                        <Form.Input
                            name={`rate-${rate.id}`}
                            value={editRate}
                            placeholder="Rate per unit"
                            onChange={e => setEditRate(e.target.value)}
                        >
                          <input />
                        </Form.Input>
                      </td>
                      <td>
                        <Dropdown
                            placeholder="Unit"
                            fluid
                            selection
                            className="field"
                            value={editUnit}
                            options={rateUnitsOptions}
                            onChange={(e, data) => {
                              setEditUnit(data.value)
                            }}
                        />
                      </td>
                      <td>
                        <Form.Input
                            name={`note-${rate.id}`}
                            type={'textbox'}
                            value={editNotes}
                            placeholder="Notes"
                            onChange={e => setEditNotes(e.target.value)}
                        />
                      </td>
                      <td>
                        <Button positive onClick={handleSave}>Save</Button>
                        <Button onClick={resetEdit}>Cancel</Button>
                      </td>
                    </tr>
                  )
                } else {
                  return (
                      <tr>
                        <td>{dateFormatter(rate.effective_date)}</td>
                        <td className="number-column">${twoDecimalFormatter(rate.utility_rate)}</td>
                        <td>{rate.utility_rate_unit}</td>
                        <td>{rate.notes}</td>
                        <td>
                          <Button 
                              onClick={() => handleEdit(rate)}
                              disabled={isEditActive}
                          >
                            Edit
                          </Button>
                          <Button
                            onClick={() => handleDelete(rate.id)}
                            disabled={isEditActive}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                  )
                }
              }))}
              </tbody>
            </table>
          </Form>
        </div>
      </Modal.Content>
    </Modal>
  );
};
